import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { getGlossaryTerm, glossaryTerms, type GlossaryTerm } from "@/content/glossary";
import { SeoFAQ } from "@/components/seo-faq";
import { AccreditationsStrip } from "@/components/accreditations-strip";
import { EabBannerBlock } from "@/components/eab-banner";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { ReviewStamp } from "@/components/review-stamp";
import { LEGAL_AUTHOR } from "@/content/authors";
import { RelatedContent } from "@/components/related-content";
import { getRelatedContent } from "@/lib/related";
import { formatDateLong } from "@/lib/format-date";
import { DeepContentSection } from "@/components/deep-content";
import { getGlossaryDepth } from "@/content/glossary-depth";
import {
  buildHead,
  generatePageTitle,
  definedTermSchema,
  faqSchema,
  ensureFAQs,
} from "@/lib/seo";
import { GLOSSARY_FAQS } from "@/content/resource-depth";

export const Route = createFileRoute("/hr-glossary/$slug")({
  loader: ({ params }) => {
    const term = getGlossaryTerm(params.slug);
    if (!term) throw notFound();
    return { term };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { term } = loaderData;
    const pk = term.primaryKeyword ?? term.term.toLowerCase();
    const path = `/hr-glossary/${term.slug}`;
    const title = generatePageTitle(pk, "glossary");
    const description =
      term.shortDefinition.length > 160
        ? term.shortDefinition.slice(0, 157) + "..."
        : term.shortDefinition;
    // Breadcrumb JSON-LD is emitted once, by the visible trail on the page.
    const faqs = ensureFAQs(pk, GLOSSARY_FAQS[term.slug] ?? [], "glossary");
    return buildHead({
      title,
      description,
      path,
      schemas: [
        definedTermSchema({ name: term.term, description: term.shortDefinition }),
        faqSchema(faqs),
      ],
    });
  },
  component: GlossaryPage,
});

function GlossaryPage() {
  const { term } = Route.useLoaderData() as { term: GlossaryTerm };
  const pk = term.primaryKeyword ?? term.term.toLowerCase();
  const related = (term.relatedTerms ?? [])
    .map((slug: string) => glossaryTerms.find((t) => t.slug === slug))
    .filter((t): t is GlossaryTerm => Boolean(t));
  const depth = getGlossaryDepth(term.slug);

  return (
    <>
      <PageHero
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Glossary", path: "/hr-glossary" },
          { label: term.term, path: `/hr-glossary/${term.slug}` },
        ]}
        eyebrow="Glossary"
        title={`What Is ${term.term}?`}
        subtitle={`${term.term} explained, a plain-English guide for UK employers.`}
        lastReviewed={term.lastReviewed}
        author={term.author ?? LEGAL_AUTHOR}
      />
      <section className="px-6 py-12 lg:px-8 lg:py-16">
        <div className="mx-auto max-w-7xl">


          <div className="mt-8 rounded-2xl border border-accent/20 bg-accent/5 p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">Short definition</p>
            <p className="mt-3 text-lg leading-relaxed text-foreground">{term.shortDefinition}</p>
          </div>

          <div className="mt-10">
            <h2 className="font-serif text-2xl text-brand">The legal definition of {term.term}</h2>
            <p className="mt-4 leading-relaxed text-foreground/90">{term.fullDefinition}</p>
          </div>

          {term.legalReference && (
            <p className="mt-8 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Legal reference:</span> {term.legalReference}
            </p>
          )}

          <p className="mt-4 text-xs text-muted-foreground">
            Last reviewed {formatDateLong(term.lastReviewed)}.
          </p>

          {term.hubService && (
            <div className="mt-10 rounded-2xl border border-accent/30 bg-accent/5 p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent">Need help in practice?</p>
              <p className="mt-3 text-base leading-relaxed text-foreground">
                This page covers the legal definition only. For end-to-end employer support, see our{" "}
                <Link
                  to="/services/$slug"
                  params={{ slug: term.hubService.slug }}
                  className="font-medium text-brand underline decoration-accent underline-offset-4 hover:text-accent"
                >
                  {term.hubService.name}
                </Link>{" "}
                service.
              </p>
            </div>
          )}

          {related.length > 0 && (
            <div className="mt-12 rounded-2xl border border-border bg-card p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Related terms</p>
              <ul className="mt-4 space-y-2">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to="/hr-glossary/$slug"
                      params={{ slug: r.slug }}
                      className="font-serif text-base text-brand hover:text-accent"
                    >
                      {r.term}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {depth && <DeepContentSection content={depth} />}

      <EabBannerBlock path={`/hr-glossary/${term.slug}`} source="glossary_page" />

      <AccreditationsStrip />
      <SeoFAQ
        primaryKeyword={pk}
        faqs={GLOSSARY_FAQS[term.slug] ?? []}
        type="glossary"
        heading={`Questions employers ask about ${term.term.replace(/\s*\(.*\)$/, "").toLowerCase()}`}
      />

      <RelatedContent
        heading="Related terms and guides"
        items={getRelatedContent({
          type: "glossary",
          slug: term.slug,
          primaryKeyword: pk,
          topics: term.relatedTerms ?? [],
          diversify: true,
        })}
      />
    </>
  );
}
